import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_ADD_PRODUCT_SOP,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { ADD_PRODUCT_ITEM_SOP_SECTIONS } from "./constants/addProductItemSopContent";
import { ADD_PRODUCT_ITEM_SOP_IMAGES } from "./constants/addProductItemSopImages";
import type { AddProductItemSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const AddProductItemSopImage: React.FC<{
  image: AddProductItemSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const AddProductItemSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const aboutProductPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ABOUT_PRODUCT, language, defaultLang)
      : `/${PAGE_ROUTE_ABOUT_PRODUCT}`;
  const sopsHubPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const addProductSopPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ADD_PRODUCT_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_ADD_PRODUCT_SOP}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="add-product-item-sop-page"
        eyebrow={t("add-product-item-sop-page-eyebrow", "Run books")}
        title={t(
          "add-product-item-sop-page-title",
          "How to add a product item"
        )}
        intro={t(
          "add-product-item-sop-page-intro",
          "Record stock for an existing product, set prices for the batch, and print QR labels for faster checkout."
        )}
      />

      <PublicInfoCallout testId="add-product-item-sop-page-note">
        {t(
          "add-product-item-sop-page-note",
          "Required fields are marked with *. The product must already exist in your catalog before you can add items."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {ADD_PRODUCT_ITEM_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`add-product-item-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? ADD_PRODUCT_ITEM_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <AddProductItemSopImage
                        image={image}
                        testId={`add-product-item-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t(
          "add-product-item-sop-page-cta-title",
          "Stock recorded. What next?"
        )}
        testId="add-product-item-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "add-product-item-sop-page-cta-body",
            "Attach QR labels to shelves or packaging, then sell or transfer items from the Product Items list."
          )}{" "}
          <MuiLink
            component={Link}
            to={sopsHubPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-item-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("add-product-item-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={aboutProductPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-product-item-sop-page-cta-about-product-link",
              "Products vs. product items"
            )}
          </MuiLink>
          {t("add-product-item-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={addProductSopPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-product-item-sop-page-cta-add-product-link",
              "How to add a product"
            )}
          </MuiLink>
          {t("add-product-item-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-item-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("add-product-item-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-item-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default AddProductItemSopPage;
